// Steg som ägs av roller utanför systemet – kvitteras här av den som arbetar ensam.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { stepsOutsideSystem, rolesOutsideSystem } from "@/lib/basProcess/soloMode";
import type { ProcessStepDef } from "@/lib/basProcess/constants";
import type { ProjectRole } from "@/lib/basProcess/roleViews";
import { CheckCircle2, ExternalLink } from "lucide-react";

interface Props {
  projectId: string;
  ownRole: ProjectRole;
  rolesInSystem: ProjectRole[];
  canEdit: boolean;
}

interface ExternalRow {
  doc_number: string;
  completed: boolean;
  counterpart_name: string | null;
  completed_at: string | null;
  note: string | null;
  url: string | null;
  review_status: string | null;
  review_comment: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
}

const REVIEW_LABEL: Record<string, string> = {
  ej_granskad: "Ej granskad",
  godkand: "Granskad och godkänd",
  komplettera: "Behöver kompletteras",
};

export const CounterpartSteps: React.FC<Props> = ({
  projectId,
  ownRole,
  rolesInSystem,
  canEdit,
}) => {
  const [rows, setRows] = useState<Record<string, ExternalRow>>({});
  const [active, setActive] = useState<ProcessStepDef | null>(null);
  const [form, setForm] = useState({ counterpart_name: "", note: "", url: "" });
  const [saving, setSaving] = useState(false);
  const [review, setReview] = useState<{ doc: string; status: string } | null>(null);
  const [reviewForm, setReviewForm] = useState({ name: "", comment: "" });

  const steps = stepsOutsideSystem(rolesInSystem, ownRole);
  const outsideRoles = rolesOutsideSystem(rolesInSystem, ownRole);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("external_step_completions")
      .select(
        "doc_number, completed, counterpart_name, completed_at, note, url, review_status, review_comment, reviewed_by_name, reviewed_at"
      )
      .eq("project_id", projectId);
    const map: Record<string, ExternalRow> = {};
    (data || []).forEach((r) => {
      map[(r as ExternalRow).doc_number] = r as ExternalRow;
    });
    setRows(map);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  if (steps.length === 0) return null;

  const openDialog = (step: ProcessStepDef) => {
    const existing = rows[step.docNumber];
    setForm({
      counterpart_name: existing?.counterpart_name || "",
      note: existing?.note || "",
      url: existing?.url || "",
    });
    setActive(step);
  };

  const register = async () => {
    if (!active) return;
    setSaving(true);
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("external_step_completions").upsert(
      {
        project_id: projectId,
        doc_number: active.docNumber,
        completed: true,
        completed_at: new Date().toISOString(),
        counterpart_name: form.counterpart_name.trim() || null,
        note: form.note.trim() || null,
        url: form.url.trim() || null,
        registered_by: auth.user?.id ?? null,
      },
      { onConflict: "project_id,doc_number" }
    );
    setSaving(false);
    if (error) {
      toast.error("Kunde inte registrera");
      return;
    }
    toast.success("Registrerat utifrån");
    setActive(null);
    load();
  };

  const openReview = (doc: string, status: string, row: ExternalRow) => {
    setReviewForm({ name: row.reviewed_by_name || "", comment: row.review_comment || "" });
    setReview({ doc, status });
  };

  const saveReview = async () => {
    if (!review) return;
    if (!reviewForm.name.trim()) {
      toast.error("Skriv vem som granskat");
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("external_step_completions")
      .update({
        review_status: review.status,
        review_comment: reviewForm.comment.trim() || null,
        reviewed_by_name: reviewForm.name.trim(),
        reviewed_at: new Date().toISOString(),
      })
      .eq("project_id", projectId)
      .eq("doc_number", review.doc);
    setSaving(false);
    if (error) {
      toast.error("Kunde inte spara granskningen");
      return;
    }
    toast.success("Granskning sparad");
    setReview(null);
    load();
  };


  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ExternalLink className="h-4 w-4" /> Sköts utanför systemet
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {outsideRoles.join(", ")} arbetar inte i systemet. Registrera vad de har levererat så
            fastnar inte processen – det märks tydligt som registrerat utifrån.
          </p>
          <div className="space-y-2">
            {steps.map((s) => {
              const row = rows[s.docNumber];
              return (
                <div
                  key={s.docNumber}
                  className="flex flex-wrap items-center gap-2 rounded-md border p-2"
                >
                  <Badge variant="outline" className="text-[10px]">
                    Dok {s.docNumber}
                  </Badge>
                  <span className="text-sm">{s.title}</span>
                  {row?.completed ? (
                    <Badge variant="secondary" className="ml-auto gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Registrerat utifrån
                      {row.counterpart_name ? ` – ${row.counterpart_name}` : ""}
                    </Badge>
                  ) : (
                    <span className="ml-auto text-xs text-muted-foreground">Ej registrerat</span>
                  )}
                  {canEdit && (
                    <Button size="sm" variant="ghost" onClick={() => openDialog(s)}>
                      {row?.completed ? "Ändra" : "Registrera"}
                    </Button>
                  )}
                  {row?.completed && (
                    <div className="flex w-full flex-wrap items-center gap-2 border-t pt-2">
                      <Badge
                        variant={
                          row.review_status === "godkand"
                            ? "secondary"
                            : row.review_status === "komplettera"
                              ? "destructive"
                              : "outline"
                        }
                        className="text-[10px]"
                      >
                        {REVIEW_LABEL[row.review_status || "ej_granskad"]}
                        {row.reviewed_by_name ? ` – ${row.reviewed_by_name}` : ""}
                      </Badge>
                      {row.review_comment && (
                        <span className="text-xs text-muted-foreground">{row.review_comment}</span>
                      )}
                      {canEdit && (
                        <div className="ml-auto flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReview(s.docNumber, "godkand", row)}
                          >
                            Godkänn
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openReview(s.docNumber, "komplettera", row)}
                          >
                            Begär komplettering
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {review?.status === "godkand"
                ? "Godkänn extern leverans"
                : "Begär komplettering"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Ditt namn</Label>
              <Input
                value={reviewForm.name}
                onChange={(e) => setReviewForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Den som granskat"
              />
            </div>
            <div className="space-y-1">
              <Label>Kommentar</Label>
              <Textarea
                value={reviewForm.comment}
                onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
                placeholder={
                  review?.status === "godkand"
                    ? "Vad granskades och vad bedömdes vara tillräckligt"
                    : "Vad saknas och vad behöver kompletteras"
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReview(null)}>
              Avbryt
            </Button>
            <Button onClick={saveReview} disabled={saving}>
              {saving ? "Sparar…" : "Spara granskning"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Registrera Dok {active?.docNumber} – {active?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Vem levererade?</Label>
              <Input
                value={form.counterpart_name}
                onChange={(e) => setForm((f) => ({ ...f, counterpart_name: e.target.value }))}
                placeholder="Namn eller företag"
              />
            </div>
            <div className="space-y-1">
              <Label>Anteckning</Label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="Vad togs emot, när och i vilken form"
              />
            </div>
            <div className="space-y-1">
              <Label>Länk till deras dokument (valfritt)</Label>
              <Input
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActive(null)}>
              Avbryt
            </Button>
            <Button onClick={register} disabled={saving}>
              {saving ? "Sparar…" : "Registrera som klart"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CounterpartSteps;
